import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, Outlet, redirect, useParams, useRouterState } from '@tanstack/react-router'
import { Banknote, BriefcaseBusiness, Building2, CalendarClock, Contact, Crown, DoorOpen, LayoutDashboard, LogOut, MapPin, Menu, Package, Settings, ShieldCheck, Store, Ticket, UserRound, Users, Wallet, X } from 'lucide-react'
import { Dialog } from 'radix-ui'
import { useState, type ReactNode } from 'react'
import { api } from '@/api'
import { accessQuery, godMeQuery, meQuery, orgsQuery } from '@/api/queries'
import { CompanySwitcher } from '@/app/company-switcher'
import { signOut } from '@/app/session'
import { callerOf, companySections, WORK_SECTIONS } from '@/domain/abilities'
import { errorText } from '@/domain/errors'
import { t } from '@/i18n'
import { AccountMenu } from '@/ui/account-menu'
import { AuthFrame } from '@/ui/auth-frame'
import { Button, IconButton } from '@/ui/button'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'
import { Wordmark } from '@/ui/wordmark'

export const Route = createFileRoute('/_app')({
  beforeLoad: () => {
    if (!api.hasSession()) throw redirect({ to: '/sign-in' })
  },
  component: AppShell,
})

/// Signed-in frame: a fixed 256 px sidebar (the iOS tab bar, turned sideways
/// for a desktop) parted from the content by a hairline, and the content
/// column, whose pages all start at the same left edge (see `Page`).
/// Narrower than `lg` the sidebar folds into a top bar with a menu button and
/// opens as a drawer from the left. The sidebar lists ONLY what exists for
/// this person: the gods' console for gods, a company's sections by its
/// `/access` answer. Someone who belongs nowhere gets an empty state and no
/// nav at all.
function AppShell() {
  const god = useQuery(godMeQuery())
  const orgs = useQuery(orgsQuery())
  const me = useQuery(meQuery())

  if (god.isPending || orgs.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingBlock label={t.app.loading} />
      </div>
    )
  }

  if (god.isError || orgs.isError) {
    return (
      <Bare>
        <InlineBanner
          kind="error"
          message={errorText(god.error ?? orgs.error)}
          action={{
            label: t.app.retry,
            onClick: () => {
              void god.refetch()
              void orgs.refetch()
            },
          }}
        />
      </Bare>
    )
  }

  if (!god.data && orgs.data.length === 0) {
    return (
      <Bare email={me.data?.email}>
        <EmptyState
          icon={<Store />}
          title={t.shell.noCompaniesTitle}
          message={t.shell.noCompaniesMessage}
          action={
            <Button variant="secondary" icon={<LogOut className="size-4" />} onClick={() => void signOut()}>
              {t.shell.signOut}
            </Button>
          }
        />
      </Bare>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-stretch bg-bg lg:flex-row lg:items-start">
      <MobileBar god={god.data} companies={orgs.data} name={me.data?.name} email={me.data?.email} />
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 overflow-y-auto shadow-[inset_-1px_0_0_var(--surface-3)] lg:flex">
        <Sidebar god={god.data} companies={orgs.data} name={me.data?.name} email={me.data?.email} />
      </aside>
      <main className="min-w-0 flex-1">
        <Outlet />
      </main>
    </div>
  )
}

/// A page without the sidebar — for the moments there is nothing to navigate.
const Bare = ({ children, email }: { children: ReactNode; email?: string | undefined }) => (
  <AuthFrame aside={email} width="medium">
    <div className="pt-[4vh]">{children}</div>
  </AuthFrame>
)

const NavItem = ({
  to,
  params,
  icon,
  children,
}: {
  to: string
  params?: Record<string, string>
  icon: ReactNode
  children: ReactNode
}) => (
  <Link
    to={to}
    {...(params ? { params } : {})}
    className="group flex min-h-11 items-center gap-2.5 rounded-[10px] px-2.5 text-callout font-medium text-text-2 transition-colors hover:text-text hover:tint-hover data-[status=active]:text-text data-[status=active]:tint-active lg:min-h-9"
    activeProps={{ 'aria-current': 'page' }}
  >
    <span
      aria-hidden="true"
      className="flex w-5 justify-center text-muted transition-colors group-hover:text-text-2 group-data-[status=active]:text-text [&_svg]:size-[18px] [&_svg]:stroke-[1.9]"
    >
      {icon}
    </span>
    {children}
  </Link>
)

const NavGroup = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="flex flex-col gap-px">
    <SectionHeader title={title} inset={false} className="px-2.5 pb-1.5" />
    {children}
  </div>
)

type ShellProps = {
  god: boolean
  companies: readonly import('@/api/types').Organization[]
  name: string | undefined
  email: string | undefined
}

/// The phone's top bar: the wordmark and a menu button. The menu is the very
/// same sidebar in a drawer; it closes itself once a link takes you anywhere.
function MobileBar(props: ShellProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  // Open only for the page it was opened on: following a link closes it.
  const [openOn, setOpenOn] = useState<string | null>(null)

  return (
    <Dialog.Root open={openOn === pathname} onOpenChange={(open) => setOpenOn(open ? pathname : null)}>
      <header className="sticky top-0 z-30 flex h-14 items-center gap-1 bg-bg/90 px-2 shadow-[inset_0_-1px_0_var(--surface-3)] backdrop-blur-md lg:hidden">
        <Dialog.Trigger asChild>
          <IconButton label={t.shell.openMenu}>
            <Menu className="size-5" />
          </IconButton>
        </Dialog.Trigger>
        <Link to="/" aria-label="eatlete" className="inline-flex">
          <Wordmark className="h-[20px] w-auto text-text" />
        </Link>
      </header>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim data-[state=open]:animate-[fade-in_150ms_ease-out] lg:hidden" />
        <Dialog.Content className="fixed inset-y-0 left-0 z-50 flex w-[min(304px,calc(100vw-48px))] flex-col overflow-y-auto rounded-r-[20px] bg-bg shadow-e3 outline-none data-[state=open]:animate-[drawer-in_200ms_cubic-bezier(0.32,0.72,0,1)] lg:hidden">
          <Dialog.Title className="sr-only">{t.shell.menu}</Dialog.Title>
          <Dialog.Description className="sr-only">{t.shell.menu}</Dialog.Description>
          <Dialog.Close asChild>
            <IconButton label={t.app.close} className="absolute top-[14px] right-2">
              <X className="size-5" />
            </IconButton>
          </Dialog.Close>
          <Sidebar {...props} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function Sidebar({ god, companies, name, email }: ShellProps) {
  const params = useParams({ strict: false }) as { orgId?: string }
  const orgId = params.orgId ?? null
  const access = useQuery({ ...accessQuery(orgId ?? ''), enabled: Boolean(orgId) })
  const caller = access.data ? callerOf(access.data) : null
  const sections = caller ? companySections(caller) : []
  // A trainer's day starts at «Моя робота»; everybody else finds it after
  // the club's sections.
  const ownFirst = caller?.profile.scope === 'own'
  const myWork =
    orgId && sections.includes('mywork') ? (
      <NavItem to="/c/$orgId/my-work" params={{ orgId }} icon={<BriefcaseBusiness />}>
        {t.payroll.myWork.nav}
      </NavItem>
    ) : null
  // Everyone who works here has a profile of their own (specs/staff-profiles.md
  // SP3) — a god from outside does not.
  const member = Boolean(access.data && access.data.actor !== 'eatlete')

  return (
    <nav aria-label={t.shell.menu} className="flex min-h-full w-full flex-1 flex-col gap-6 px-3 pt-5 pb-3">
      <div className="flex h-9 items-center px-2.5">
        <Link to="/" aria-label="eatlete" className="inline-flex rounded-[6px]">
          <Wordmark className="h-[21px] w-auto text-text" />
        </Link>
      </div>

      {companies.length > 0 ? <CompanySwitcher companies={companies} currentId={orgId} /> : null}

      {orgId && sections.some((s) => WORK_SECTIONS.includes(s)) ? (
        <NavGroup title={t.shell.workSection}>
          {ownFirst ? myWork : null}
          {sections.includes('overview') ? (
            <NavItem to="/c/$orgId/overview" params={{ orgId }} icon={<LayoutDashboard />}>
              {t.overview.nav}
            </NavItem>
          ) : null}
          {sections.includes('clients') ? (
            <NavItem to="/c/$orgId/clients" params={{ orgId }} icon={<Contact />}>
              {t.shell.clients}
            </NavItem>
          ) : null}
          {sections.includes('passes') ? (
            <NavItem to="/c/$orgId/passes" params={{ orgId }} icon={<Ticket />}>
              {t.passes.nav}
            </NavItem>
          ) : null}
          {sections.includes('goods') ? (
            <NavItem to="/c/$orgId/goods" params={{ orgId }} icon={<Package />}>
              {t.goods.nav}
            </NavItem>
          ) : null}
          {sections.includes('services') ? (
            <NavItem to="/c/$orgId/services" params={{ orgId }} icon={<CalendarClock />}>
              {t.services.nav}
            </NavItem>
          ) : null}
          {sections.includes('cash') ? (
            <NavItem to="/c/$orgId/cash" params={{ orgId }} icon={<Banknote />}>
              {t.cash.nav}
            </NavItem>
          ) : null}
          {sections.includes('payroll') ? (
            <NavItem to="/c/$orgId/payroll" params={{ orgId }} icon={<Wallet />}>
              {t.payroll.nav}
            </NavItem>
          ) : null}
          {sections.includes('entry') ? (
            <NavItem to="/c/$orgId/entry" params={{ orgId }} icon={<DoorOpen />}>
              {t.access.nav}
            </NavItem>
          ) : null}
          {ownFirst ? null : myWork}
        </NavGroup>
      ) : null}

      {orgId && (member || sections.some((s) => !WORK_SECTIONS.includes(s))) ? (
        <NavGroup title={t.shell.companySection}>
          {sections.includes('staff') ? (
            <NavItem to="/c/$orgId/staff" params={{ orgId }} icon={<Users />}>
              {t.shell.staff}
            </NavItem>
          ) : null}
          {sections.includes('roles') ? (
            <NavItem to="/c/$orgId/roles" params={{ orgId }} icon={<ShieldCheck />}>
              {t.shell.roles}
            </NavItem>
          ) : null}
          {sections.includes('branches') ? (
            <NavItem to="/c/$orgId/branches" params={{ orgId }} icon={<MapPin />}>
              {t.shell.branches}
            </NavItem>
          ) : null}
          {sections.includes('settings') ? (
            <NavItem to="/c/$orgId/settings" params={{ orgId }} icon={<Settings />}>
              {t.shell.settings}
            </NavItem>
          ) : null}
          {member ? (
            <NavItem to="/c/$orgId/profile" params={{ orgId }} icon={<UserRound />}>
              {t.shell.myProfile}
            </NavItem>
          ) : null}
        </NavGroup>
      ) : null}

      {orgId && access.data?.actor === 'eatlete' ? (
        <p className="-mt-2 px-2.5 text-caption text-text-2">{t.shell.asEatlete}</p>
      ) : null}

      {god ? (
        <NavGroup title={t.shell.godsSection}>
          <NavItem to="/gods/companies" icon={<Building2 />}>
            {t.shell.godsCompanies}
          </NavItem>
          <NavItem to="/gods/list" icon={<Crown />}>
            {t.shell.godsList}
          </NavItem>
        </NavGroup>
      ) : null}

      <div className="mt-auto">
        <AccountMenu name={name} email={email} onSignOut={() => void signOut()} />
      </div>
    </nav>
  )
}
